import { computed, onMounted, ref, onScopeDispose } from 'vue'
import { useState } from '#app'

type PerformanceMode = 'standard' | 'low'

const STORAGE_KEY = 'orion-performance-mode'

const isClient = typeof window !== 'undefined'

const getNavigator = () => (navigator as Navigator & { deviceMemory?: number; connection?: any })

const evaluateMode = (): PerformanceMode => {
  if (!isClient) return 'standard'

  let shouldReduce = false
  const nav = getNavigator()

  const deviceMemory = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined
  if (deviceMemory && deviceMemory <= 4) {
    shouldReduce = true
  }

  const logicalCores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : undefined
  if (logicalCores && logicalCores <= 4) {
    shouldReduce = true
  }

  if (nav.connection?.saveData === true) {
    shouldReduce = true
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  if (reduceMotion.matches) {
    shouldReduce = true
  }

  const reduceData = window.matchMedia('(prefers-reduced-data: reduce)')
  if (reduceData.matches) {
    shouldReduce = true
  }

  return shouldReduce ? 'low' : 'standard'
}

export function usePerformanceMode() {
  const state = useState('performance-mode', () => ({
    mode: 'standard' as PerformanceMode,
    userOverride: false,
    hydrated: false,
  }))

  const reduceMotionQuery = ref<MediaQueryList | null>(null)
  const reduceDataQuery = ref<MediaQueryList | null>(null)

  const applySystemPreference = () => {
    if (!isClient || state.value.userOverride) return
    state.value.mode = evaluateMode()
  }

  const readStoredPreference = (): PerformanceMode | null => {
    if (!isClient) return null
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'low' || stored === 'standard') {
      return stored
    }
    return null
  }

  const persistPreference = (mode: PerformanceMode | null) => {
    if (!isClient) return
    if (mode) {
      window.localStorage.setItem(STORAGE_KEY, mode)
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  }

  if (isClient) {
    onMounted(() => {
      if (!state.value.hydrated) {
        const stored = readStoredPreference()
        if (stored) {
          state.value.mode = stored
          state.value.userOverride = true
        } else {
          state.value.mode = evaluateMode()
        }
        state.value.hydrated = true
      }

      reduceMotionQuery.value = window.matchMedia('(prefers-reduced-motion: reduce)')
      reduceDataQuery.value = window.matchMedia('(prefers-reduced-data: reduce)')

      const handlePreferenceChange = () => {
        state.value.userOverride = !!readStoredPreference()
        applySystemPreference()
      }

      reduceMotionQuery.value.addEventListener('change', handlePreferenceChange)
      reduceDataQuery.value.addEventListener('change', handlePreferenceChange)

      onScopeDispose(() => {
        reduceMotionQuery.value?.removeEventListener('change', handlePreferenceChange)
        reduceDataQuery.value?.removeEventListener('change', handlePreferenceChange)
      })
    })
  }

  const mode = computed(() => state.value.mode)
  const isLowPower = computed(() => mode.value === 'low')

  const setMode = (nextMode: PerformanceMode) => {
    state.value.mode = nextMode
    state.value.userOverride = true
    persistPreference(nextMode)
  }

  const resetMode = () => {
    state.value.userOverride = false
    persistPreference(null)
    applySystemPreference()
  }

  return {
    mode,
    isLowPower,
    setMode,
    resetMode,
  }
}

